// ./src/components/features/PollQuestions/PollQuestionEditDrawer/PollQuestionEditDrawer.tsx
//
// Create/edit drawer for an event website poll / quiz question. Self-loading in edit mode
// via recordId; context-or-props resolution; no success toast (the page highlight is the
// confirmation). Fields: question, category, description (markdown), type (single / multi
// / free text), answer options (choice types), guests-may-add-option, quiz toggle +
// correct answers, required, status, and a flat images gallery. Mirrors SignupItemEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, Input, Skeleton, Divider, Switch } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  PollQuestion,
  PollQuestionInput,
  pollQuestionImageUrl,
  eventContextPath,
  POLL_QUESTION_TYPE,
  isPollChoiceType,
} from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalPollQuestionsContext } from "@/contexts/PollQuestionsContext";
import {
  AppDrawer,
  StatusInput,
  CategorySelect,
  SegmentedField,
  MediaGalleryField,
  MediaImageRef,
} from "@/components/generic";

import { PollOptionsEditor, PollOptionDraft, makeOption, withTrailingEmptyOptions } from "./PollOptionsEditor";

const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);

export interface PollQuestionEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this question belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** The question for a NEW poll (the create wizard's one-tap ideas, "Guess the due date"). Ignored when editing. */
  defaultQuestion?: string | null;
  onCreated?: (item: PollQuestion) => void;
  onUpdated?: (item: PollQuestion) => void;
}

interface FormData {
  categoryId: string | null;
  question: string;
  description: string;
  questionType: string;
  options: PollOptionDraft[];
  allowAddOption: boolean;
  isQuiz: boolean;
  isRequired: boolean;
  photos: MediaImageRef[];
  status: number;
}

const KNOWN_FORM_FIELDS = ["categoryId", "question", "description", "questionType", "options", "status"];

const getInitialFormData = (defaultQuestion?: string | null): FormData => ({
  categoryId: null,
  question: defaultQuestion ?? "",
  description: "",
  questionType: POLL_QUESTION_TYPE.SINGLE,
  options: withTrailingEmptyOptions([]),
  allowAddOption: false,
  isQuiz: false,
  isRequired: false,
  photos: [],
  status: STATUSES.ACTIVE,
});

const optionsFromRecord = (record: PollQuestion): PollOptionDraft[] =>
  withTrailingEmptyOptions((record.options ?? []).map((o) => makeOptionFrom(o.id, o.label, o.isCorrect)));

const makeOptionFrom = (id?: string | null, label?: string | null, isCorrect?: boolean | null): PollOptionDraft => {
  const base = makeOption(label ?? "", !!isCorrect);

  return id ? { ...base, id } : base;
};

const photosFromRecord = (record: PollQuestion): MediaImageRef[] =>
  (record.images ?? []).map((i) => ({
    uploadedFileId: i.uploadedFileId ?? null,
    url: pollQuestionImageUrl(i),
  }));

const fromRecord = (record: PollQuestion): FormData => ({
  categoryId: record.categoryId || null,
  question: record.question || "",
  description: record.description || "",
  questionType: record.questionType || POLL_QUESTION_TYPE.SINGLE,
  options: optionsFromRecord(record),
  allowAddOption: !!record.allowAddOption,
  isQuiz: !!record.isQuiz,
  isRequired: !!record.isRequired,
  photos: photosFromRecord(record),
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const PollQuestionEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const PollQuestionEditDrawer: React.FC<PollQuestionEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  onCreated,
  onUpdated,
  defaultQuestion,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const ctx = useOptionalPollQuestionsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<PollQuestion | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as PollQuestion);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const optionsBaseRef = useRef<{ id?: string | null; label?: string | null }[]>([]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData(defaultQuestion);
    // #147 — the options as loaded: the save sends them so a guest's option added (or renamed) meanwhile survives.
    optionsBaseRef.current = (record?.options ?? []).map((o) => ({ id: o.id, label: o.label }));
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, defaultQuestion]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const isChoice = isPollChoiceType(formData.questionType);

  // Switching to free text clears quiz + add-option (they don't apply); switching type
  // resets nothing else, so a mistaken pick keeps the drafted options.
  const handleTypeChange = useCallback((_name: string | undefined, value: string) => {
    setFormData((prev) => {
      const nowChoice = isPollChoiceType(value);

      return {
        ...prev,
        questionType: value,
        isQuiz: nowChoice ? prev.isQuiz : false,
        allowAddOption: nowChoice ? prev.allowAddOption : false,
      };
    });
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const choice = isPollChoiceType(formData.questionType);

    // Only choice types carry options; drop blank labels, and clear correctness when
    // it isn't a quiz (the flag is ignored server-side but we keep the payload clean).
    const options = choice
      ? formData.options
          .map((o) => ({ id: o.id, label: o.label.trim(), isCorrect: formData.isQuiz ? !!o.isCorrect : false }))
          .filter((o) => o.label.length > 0)
      : [];

    const submitData: PollQuestionInput = {
      categoryId: formData.categoryId,
      question: formData.question.trim() || null,
      description: formData.description.trim() || null,
      questionType: formData.questionType,
      options,
      allowAddOption: choice ? formData.allowAddOption : false,
      isQuiz: choice ? formData.isQuiz : false,
      isRequired: formData.isRequired,
      images: formData.photos.map((i) => ({
        uploadedFileId: i.uploadedFileId ?? null,
        url: i.uploadedFileId ? null : i.url ?? null,
      })),
      status: formData.status,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    } else if (choice) {
      submitData.optionsBase = optionsBaseRef.current;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as PollQuestion[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("events:pollQuestions.form.editTitle", { defaultValue: "Edit question" })
    : t("events:pollQuestions.form.addTitle", { defaultValue: "Add question" });

  const typeData = useMemo(
    () => [
      { label: t("events:pollQuestions.form.types.single", { defaultValue: "Single choice" }), value: POLL_QUESTION_TYPE.SINGLE },
      { label: t("events:pollQuestions.form.types.multi", { defaultValue: "Multiple choice" }), value: POLL_QUESTION_TYPE.MULTI },
      { label: t("events:pollQuestions.form.types.freeText", { defaultValue: "Free text" }), value: POLL_QUESTION_TYPE.FREE_TEXT },
    ],
    [t]
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <PollQuestionEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("events:pollQuestions.form.fields.question.label", { defaultValue: "Question" })}
            placeholder={t("events:pollQuestions.form.fields.question.placeholder", {
              defaultValue: "e.g. What's your favourite session?",
            })}
            value={formData.question}
            onChange={(e) => setField("question", e.currentTarget.value)}
            error={fieldErrors.question}
            required
            data-autofocus
            size="md"
          />

          <CategorySelect
            group="poll"
            exactGroup
            label={t("events:pollQuestions.form.fields.category.label", { defaultValue: "Category" })}
            description={t("events:pollQuestions.form.fields.category.description", {
              defaultValue: "Icebreaker, trivia, feedback, preferences…",
            })}
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          <Input.Wrapper
            label={t("events:pollQuestions.form.fields.description.label", { defaultValue: "Description" })}
            description={t("events:pollQuestions.form.fields.description.hint", {
              defaultValue: "Optional context shown under the question.",
            })}
            error={fieldErrors.description}
          >
            <Suspense fallback={<Skeleton height={160} radius="sm" />}>
              <MarkdownField
                value={formData.description}
                onChange={(md) => setField("description", md)}
                placeholder={t("events:pollQuestions.form.fields.description.placeholder", {
                  defaultValue: "Any details guests should know…",
                })}
                minHeight={160}
              />
            </Suspense>
          </Input.Wrapper>

          <SegmentedField
            name="questionType"
            label={t("events:pollQuestions.form.fields.type.label", { defaultValue: "Answer type" })}
            data={typeData}
            value={formData.questionType}
            onChange={handleTypeChange}
            error={fieldErrors.questionType}
            fullWidth
            size="md"
          />

          {isChoice ? (
            <>
              <Switch
                label={t("events:pollQuestions.form.fields.isQuiz.label", { defaultValue: "Quiz question" })}
                description={t("events:pollQuestions.form.fields.isQuiz.description", {
                  defaultValue: "Mark the correct answer(s) — responses are scored.",
                })}
                checked={formData.isQuiz}
                onChange={(e) => setField("isQuiz", e.currentTarget.checked)}
                size="md"
              />

              <Input.Wrapper
                label={t("events:pollQuestions.form.fields.options.label", { defaultValue: "Answer options" })}
                error={fieldErrors.options}
              >
                <PollOptionsEditor
                  value={formData.options}
                  onChange={(options) => setField("options", options)}
                  isQuiz={formData.isQuiz}
                  questionType={formData.questionType}
                />
              </Input.Wrapper>

              <Switch
                label={t("events:pollQuestions.form.fields.allowAddOption.label", { defaultValue: "Guests can add their own option" })}
                checked={formData.allowAddOption}
                onChange={(e) => setField("allowAddOption", e.currentTarget.checked)}
                size="md"
              />
            </>
          ) : null}

          <Switch
            label={t("events:pollQuestions.form.fields.isRequired.label", { defaultValue: "Required" })}
            description={t("events:pollQuestions.form.fields.isRequired.description", {
              defaultValue: "Guests must answer this to submit.",
            })}
            checked={formData.isRequired}
            onChange={(e) => setField("isRequired", e.currentTarget.checked)}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />

          <Divider label={t("events:pollQuestions.form.photos.section", { defaultValue: "Images" })} labelPosition="left" />

          <MediaGalleryField
            value={formData.photos}
            onChange={(photos) => setField("photos", photos)}
            contextPath={eventId ? eventContextPath(eventId) : null}
            label={t("events:pollQuestions.form.photos.label", { defaultValue: "Photos" })}
            description={t("events:pollQuestions.form.photos.hint", {
              defaultValue: "Optional images shown with the question (first is the main image).",
            })}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
